import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { ApiService } from './api.service';
import {
  DayBlockDTO,
  IDayBlockDTO,
  IDayBlockPayloadDTO,
  IUpdateDayBlockPositionDto,
} from '@app/infrastructure/models/day-block';
import {
  IQuizBlockDTO,
  QuizTypes,
} from '@app/infrastructure/models/quiz-block';

import { environment } from '@env/environment';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { tap } from 'rxjs/operators';
import { NotificationService } from '../notification.service';
import { ISelectOptions } from '@app/infrastructure/models/form/select';
import { Utils } from '@app/infrastructure/utils/utils';
import { BLOCK_TYPES } from '@app/infrastructure/utils/constants';
import { IVideoEmbedBlock } from '@app/infrastructure/models/video-embed-block';
import { GradeLevel } from '@app/infrastructure/models/grade-level';
import { IWYSIWYGBlock } from '@app/infrastructure/models/wysiwyg-block';
import { IFileUpload } from '@app/infrastructure/models/file';

@Injectable({
  providedIn: 'root',
})
export class DayBlockService {
  constructor(
    private apiService: ApiService,
    private notificationService: NotificationService,
  ) {}

  public createBlock(
    gradeUnitDayId,
    payload: IDayBlockPayloadDTO,
  ): Observable<IDayBlockDTO> {
    const endpoint = `${environment.apiRoute}/blocks`;
    return this.apiService.post<IDayBlockDTO, IDayBlockPayloadDTO>(
      endpoint,
      payload,
    );
  }

  public createDayBlock(
    gradeUnitDayId: number,
    request: IDayBlockPayloadDTO,
    uploadedImages,
  ): Observable<IDayBlockDTO> {
    const endpoint = `${environment.apiRoute}/days/${gradeUnitDayId}/blocks`;
    const payload = this.setFormDataPropsPost(request, uploadedImages);
    const headers = new HttpHeaders();
    headers.set('Content-Type', 'multipart/form-data');

    return this.apiService
      .post<DayBlockDTO, FormData>(endpoint, payload, { headers })
      .pipe(
        tap(() => {
          const message = `${
            request.blockType === 'WYSIWYGBlock'
              ? 'TextBlock'
              : request.blockType
          } created.`;
          return this.notificationService.showSuccess([message], 1000);
        }),
      );
  }

  public updateDayBlockPositions(
    gradeUnitDayNumber: number,
    request: IUpdateDayBlockPositionDto[],
  ): Observable<IDayBlockDTO[]> {
    const endpoint = `${environment.apiRoute}/days/${gradeUnitDayNumber}/blocks/`;
    return this.apiService.patch<IDayBlockDTO[], IUpdateDayBlockPositionDto[]>(
      endpoint,
      request,
    );
  }

  public updateDayBlock(
    gradeUnitDayNumber: number,
    request: IDayBlockPayloadDTO,
    asFormData: boolean = true,
    uploadedImages = null,
  ): Observable<IDayBlockDTO> {
    const endpoint = `${environment.apiRoute}/days/${gradeUnitDayNumber}/blocks/${request.id}`;
    const payload = this.setFormDataPropsPatch(request, uploadedImages);
    const headers = new HttpHeaders();
    if (asFormData) headers.set('Content-Type', 'multipart/form-data');

    if (asFormData) {
      return this.apiService
        .patch<IDayBlockDTO, FormData>(endpoint, payload, { headers })
        .pipe(
          tap(() => {
            const message = `${request.blockType} updated.`;
            return this.notificationService.showSuccess([message], 1000);
          }),
        );
    } else {
      return this.apiService
        .patch<IDayBlockDTO, IDayBlockPayloadDTO>(endpoint, request, {
          headers,
        })
        .pipe(
          tap(() => {
            const message = `${request.blockType} updated.`;
            return this.notificationService.showSuccess([message], 1000);
          }),
        );
    }
  }

  public getDayBlocks(
    gradeUnitDayId: number,
    assignmentId: number = null,
    classroomId: string = null,
  ): Observable<IDayBlockDTO[]> {
    const endpoint = `${environment.apiRoute}/days/${gradeUnitDayId}/blocks`;
    let params = new HttpParams();
    if (assignmentId) {
      params = params.set('assignmentId', assignmentId);
    }
    if (classroomId) {
      params = params.set('classroomId', classroomId);
    }
    return this.apiService.get<IDayBlockDTO[]>(endpoint, { params });
  }

  public getDayBlocksByGradeUnitDay(
    gradeLevel: GradeLevel,
    unitNumber: number,
    dayNumber: number,
  ): Observable<IDayBlockDTO[]> {
    const endpoint = `${environment.apiRoute}/grades/${gradeLevel}/units/${unitNumber}/days/${dayNumber}/blocks`;

    return this.apiService.get<IDayBlockDTO[]>(endpoint);
  }

  public getBlockById(blockId: number): Observable<IDayBlockDTO> {
    const endpoint = `${environment.apiRoute}/blocks/${blockId}`;

    return this.apiService.get<IDayBlockDTO>(endpoint);
  }

  public getDayBlockById(
    gradeUnitDayId: number,
    blockId: number,
  ): Observable<IDayBlockDTO> {
    const endpoint = `${environment.apiRoute}/days/${gradeUnitDayId}/blocks/${blockId}`;

    return this.apiService.get<IDayBlockDTO>(endpoint);
  }

  public deleteDayBlock(
    gradeUnitDayId: number,
    blockId: number,
  ): Observable<IDayBlockDTO> {
    const endpoint = `${environment.apiRoute}/days/${gradeUnitDayId}/blocks/${blockId}`;

    return this.apiService.delete<IDayBlockDTO>(endpoint);
  }

  /**
   * Convert the request values to `FormData` to be able to add the `FileList` to upload.
   */
  private setFormDataPropsPost(
    request: IDayBlockPayloadDTO,
    uploadedImages,
  ): FormData {
    const props: ISelectOptions[] = [
      {
        name: 'blockType',
        value: request.blockType,
      },
      {
        name: 'position',
        value: request.position.toString(),
      },
      {
        name: 'pageId',
        value: request.pageId.toString(),
      },
    ];
    this.setFormDataByBlockType(request, props, uploadedImages);

    return Utils.buildFormData(request.file, props, 'file');
  }

  private setFormDataPropsPatch(
    request: IDayBlockPayloadDTO,
    uploadedImages,
  ): FormData {
    const props: ISelectOptions[] = [
      {
        name: 'id',
        value: request.id.toString(),
      },
      {
        name: 'blockType',
        value: request.blockType,
      },
    ];
    this.setFormDataByBlockType(request, props, uploadedImages);
    return Utils.buildFormData(request.file, props, 'file');
  }

  private setFormDataByBlockType(
    dayBlock: IDayBlockPayloadDTO,
    props: ISelectOptions[],
    uploadedImages,
  ) {
    switch (dayBlock.blockType) {
      case BLOCK_TYPES.WYSIWYGBlock:
        const wysiwygBlock = dayBlock as IWYSIWYGBlock;
        props.push({
          name: 'html',
          value: wysiwygBlock.html,
        });
        break;
      case BLOCK_TYPES.VideoEmbedBlock:
        const videoEmbedBlock = dayBlock as IVideoEmbedBlock;
        props.push({
          name: 'url',
          value: videoEmbedBlock.url,
        });
        break;
      case BLOCK_TYPES.QuizBlock:
        const quizBlock = { ...(dayBlock as IQuizBlockDTO) };
        props.push({
          name: 'quizType',
          value: quizBlock.quizType,
        });
        this.setPropsByFormType(quizBlock, props, uploadedImages);
        break;
      default:
        break;
    }
  }

  private setPropsByFormType(
    quizBlock: IQuizBlockDTO,
    props: ISelectOptions[],
    uploadedImages,
  ) {
    switch (quizBlock.quizType) {
      case QuizTypes.MULTIPLECHOICE:
        for (const option of quizBlock?.options) {
          let bucket = '';
          let name = '';
          let location = '';
          let id = '';
          uploadedImages?.forEach((item) => {
            if (item.position === option.position) {
              bucket = item.fileBucket;
              name = item.fileName;
              location = item.fileLocation;
              id = item.fileId;
            }
          });
          const fileId = id ? id : option.fileId;
          const fileBucket = bucket ? bucket : option.fileBucket;
          const fileName = name ? name : option.fileName;
          const fileLocation = location ? location : option.fileLocation;

          props.push(
            {
              name: `options[${option.position}][answer]`,
              value: option.answer,
            },
            {
              name: `options[${option.position}][position]`,
              value: `${option.position}`,
            },
            {
              name: `options[${option.position}][correct]`,
              value: `${option.correct}`,
            },
            {
              name: `options[${option.position}][fileId]`,
              value: `${fileId}`,
            },
            {
              name: `options[${option.position}][fileBucket]`,
              value: `${fileBucket}`,
            },
            {
              name: `options[${option.position}][fileName]`,
              value: `${fileName}`,
            },
            {
              name: `options[${option.position}][fileLocation]`,
              value: `${fileLocation}`,
            },
          );
        }
        props.push({
          name: 'text',
          value: '',
        });
        break;
      case QuizTypes.FREEANSWER:
        props.push({
          name: 'text',
          value: quizBlock.text,
        });
        break;
      case QuizTypes.BOOLEANCHOICE:
        if (quizBlock.trueOrFalse === null) {
          props.push({
            name: 'trueOrFalse',
            value: null,
          });
        } else {
          props.push({
            name: 'trueOrFalse',
            value: quizBlock.trueOrFalse ? 'true' : 'false',
          });
        }
        break;
    }
  }
}
